import { InferredRfqColumnAndValueMappings, RfqTemplate } from '@lune-fe/dapi-models'
import { getTradeLaneLabel, getTransitCorridorLabel } from '@lune-fe/lune-components-lib'
import { countriesList } from '@lune-fe/lune-ui-lib'
import { Workbook } from 'exceljs'

import {
    accountCurrencyCodeArray,
    aircraftTypesArray,
    ColumnPropertyMapping,
    complexRoadTransportModeArray,
    countryCodeArray,
    distanceUnitsArray,
    loadUnitsArray,
    MappingDataSheet,
    ParameterName,
    PropertyMapping,
    RfqTemplateData,
    roadFuelTypesArray,
    roadVehiclesArray,
    RouteMappingData,
    TemplateLegInfo,
    tradelanesArray,
    transitCorridorsArray,
    TransportMode,
    transportModeArray,
    TransportModeGroup,
    TransportModeMapping,
    TransportModeMappingData,
    ValueMappingInformation,
} from 'views/CalculateEmissions/RFQ/utils/sharedLogic'

import { DataSheetJobSettings } from '../modals/NewCalculation'

import { getCountryCodeLabel, getRoadFuelLabel, getRoadVehicleLabel } from './utils'

/**
 * Provides a more type-safe variant of {@link RfqTemplate}.
 *
 * DAPI types `RfqTemplate.template` as `RfqTemplateMapping` (effectively an empty object type),
 * so we replace it with the concrete {@link RfqTemplateData} shape used by the app.
 */
export type TypedDapiRfqTemplate = Omit<RfqTemplate, 'template'> & {
    template: RfqTemplateData
}

export type RFQBaseData =
    | {
          sheetType: 'rfq'
          workbook: Workbook
          workbookBase64: string
      }
    | {
          sheetType: 'logistics_user_template'
          workbook: File
          workbookBase64: string
      }

export type RFQTemplateOptions = Pick<RfqTemplateData, 'sheetName' | 'headerRow'> & {
    endRow: number
    numberOfLegs: number
}

type RFQWorkingData = {
    sheet: MappingDataSheet
    headers: string[]
    headerStartIndex: number
    exampleRow: string[]
    rfqTemplate?: {
        id: string
        name: string
    }
    llmResults?: InferredRfqColumnAndValueMappings
}

export type RFQTemplateData = {
    load: RfqTemplateData['load'] | undefined
    loadSize: RfqTemplateData['loadSize'] | undefined
    shippedAt: RfqTemplateData['shippedAt'] | undefined
    shipmentId: RfqTemplateData['shipmentId'] | undefined
    shipmentIdAsIdempotencyKey?: PropertyMapping<boolean>
    clientAccountName: RfqTemplateData['clientAccountName'] | undefined
    clientAccountCurrency: RfqTemplateData['clientAccountCurrency'] | undefined
    currentLeg: number
    legs: TemplateLegInfo[]

    // headerRow is not in this type – should the inferredHeaderRow property also live elsewhere?
    inferredHeaderRow?: number

    autoFillState: AutoFillState
}

export type RFQTemplateState = RFQBaseData &
    RFQTemplateOptions &
    RFQWorkingData &
    RFQTemplateData & { jobSettings?: DataSheetJobSettings }

// Type used to interact with session storage at all points of the flow
export type RFQState = RFQBaseData &
    Partial<RFQTemplateOptions> &
    Partial<RFQWorkingData> &
    Partial<RFQTemplateData> & { jobSettings?: DataSheetJobSettings }

/**
 * Represents a mapping of transport modes where all properties are optional.
 * This type is used in scenarios where the transport mode column may be intentionally
 * unset during user edits, allowing for flexibility in user input.
 */
export type OptionalTransportModeMapping = Partial<TransportModeMapping> | undefined

export type ExtraContextLabel = 'Origin' | 'Destination'

export const unitAliases: { [key: string]: string[] } = {
    t: ['ton', 'tonne', 'tonnes', 'tons', 'metric ton', 'metric tonne', 'mt', 'mton'],
    kg: ['kilogram', 'kilograms', 'kilo', 'kilos', 'kg'],
    TEU: [
        'teu',
        'teus',
        '20dc',
        '20 ft container',
        '20ft container',
        '20 foot container',
        'twenty foot equivalent unit',
        'tons 20dc',
    ],
}

export const inputAliases: { [key: string]: string[] } = {
    // Tradelanes
    feeb: ['disaggregated_asia_to_north_europe'],
    fewb: ['disaggregated_asia_to_mediterranean'],
    transpacific: ['aggregated_transpacific'],
    transatlantic: ['aggregated_transatlantic'],
    transsuez: ['aggregated_transsuez'],
    panama: ['aggregated_panama_trade'],
    other: ['aggregated_other'],
    misc: ['aggregated_other'],

    // Booleans
    yes: ['true'],
    y: ['true'],
    on: ['true'],
    enabled: ['true'],
    '✓': ['true'],
    no: ['false'],
    n: ['false'],
    off: ['false'],
    disabled: ['false'],
    '✗': ['false'],

    // Aircrafts
    'jumbo jet': ['B744'], // popular nickname for 747-400
    dreamliner: ['B789'], // nickname for 787-9 (B789)

    // Trucks
    van: ['truck_generic_van'],
    truck: ['truck_generic_van'],

    // Fuels
    petrol: ['gasoline'],
    'b7 diesel': ['99_diesel_1_biodiesel'], // typical B7 = 7% biodiesel, closest here is 1%
    'b5 diesel': ['95_diesel_5_biodiesel'],
    'b10 diesel': ['90_diesel_10_biodiesel'],
    'b20 diesel': ['80_diesel_20_biodiesel'],
    'b50 diesel': ['50_diesel_50_biodiesel'],
    ethanol: ['ethanol_from_corn'],
    'liquefied petroleum gas': ['lpg'],
    'compressed natural gas': ['cng'],
}

// Dynamically add country aliases
for (const [name, iso2, iso3] of countriesList) {
    inputAliases[iso2.toLowerCase()] = [iso3]
    inputAliases[name.toLowerCase()] = [iso3]
}

/**
 * Names of countries aren't always the same and can sometimes be told in the original language.
 * To make inference work on these cases, provide a few aliases for countries.
 * `label` represents the country name/alias, what we are matching with user input.
 * `value` is the corresponding ISO3 country code.
 */
export const extraCountryAliases: { label: string; value: string }[] = [
    { label: 'Republic of the Congo', value: 'COD' },
    { label: 'Ivory Coast', value: 'CIV' },
]

export type ExpectedRfqValueTypes =
    | 'locode'
    | 'string'
    | 'number'
    | 'positive_number'
    | 'non_negative_number'
    | 'unknown'
    | 'airport_code'
    | 'latitude'
    | 'longitude'
    | 'vessel_imo'
    | 'date'
    | 'datetime'
export const parameterValueType: Record<ParameterName, ExpectedRfqValueTypes> = {
    clientAccountName: 'string',
    clientAccountCurrency: 'string',
    shippedAt: 'datetime',
    shipmentId: 'string',
    shipmentIdAsIdempotencyKey: 'unknown',
    load: 'positive_number',
    loadSize: 'non_negative_number', // Field is optional and BE handles zero value
    refrigeration: 'unknown',
    tradelane: 'unknown',
    countryCode: 'string',
    streetLine: 'string',
    city: 'string',
    postcode: 'unknown',
    airportCode: 'airport_code',
    portCode: 'locode',
    lat: 'latitude',
    lon: 'longitude',
    distance: 'positive_number',
    aircraftType: 'string',
    vehicleType: 'string',
    fuel: 'string',
    transportMode: 'string',
    mode: 'string',
    vesselImo: 'vessel_imo',
    vesselName: 'string',
    flightNumber: 'string',
    departureOn: 'date',
    arrivalOn: 'date',
    transitCorridor: 'unknown',
    restrictedTransitCorridor: 'unknown',
}

const mappedProperties = [
    'refrigeration',
    'tradelane',
    'transitCorridor',
    'restrictedTransitCorridor',
    'countryCode',
    'aircraftType',
    'vehicleType',
    'fuel',
    'mode',
] as const
export function isMappedProperty(
    maybeCategoryName: unknown,
): maybeCategoryName is MappedPropertyName {
    return (
        typeof maybeCategoryName === 'string' &&
        mappedProperties.includes(maybeCategoryName as MappedPropertyName)
    )
}

export type MappedPropertyName = Extract<ParameterName, (typeof mappedProperties)[number]>
// TODO centralise other parameters like formatter & mapping type. This allows type improvement
export const PARAMETER_MAPPING_OUTPUT = {
    // Client account mapping
    clientAccountName: [], // Direct mapping
    clientAccountCurrency: accountCurrencyCodeArray,

    // Unit mapping
    // We don't want to present TEU in the `load` parameter and will use `loadSize` for that
    load: loadUnitsArray.filter((t) => t !== 'TEU'),
    distance: distanceUnitsArray,

    // Complex mapping
    mode: transportModeArray,
    transportMode: transportModeArray,
    refrigeration: [false, true] as readonly boolean[],
    shipmentIdAsIdempotencyKey: [false, true] as readonly boolean[],
    tradelane: tradelanesArray,
    transitCorridor: transitCorridorsArray,
    restrictedTransitCorridor: transitCorridorsArray,
    aircraftType: aircraftTypesArray,
    fuel: roadFuelTypesArray,
    vehicleType: roadVehiclesArray,
    countryCode: countryCodeArray,

    // Direct mapping
    shippedAt: [],
    shipmentId: [],
    loadSize: [],
    vesselName: [],
    vesselImo: [],
    flightNumber: [],
    departureOn: [],
    arrivalOn: [],
    streetLine: [],
    city: [],
    postcode: [],
    airportCode: [],
    portCode: [],
    lat: [],
    lon: [],
}
export const parameterMappingOutputFormatter: Record<MappedPropertyName, (value: any) => string> = {
    refrigeration: String,
    tradelane: (val) => getTradeLaneLabel(val),
    transitCorridor: (val) => getTransitCorridorLabel(val),
    restrictedTransitCorridor: (val) => getTransitCorridorLabel(val),
    countryCode: getCountryCodeLabel,
    aircraftType: String,
    vehicleType: getRoadVehicleLabel,
    fuel: getRoadFuelLabel,
    mode: String,
}

export type ColumnPropertyValueMapping<T> = {
    source: 'column'
    columnIndex: number
    valueMapping: Extract<ValueMappingInformation<T>, { source: 'mapping' }>
}

export type LegLevelMissingMappingData = {
    type: 'legLevel'
    name: Extract<MappedPropertyName, 'mode' | 'countryCode'>
    unmappedValues: string[]
} & Required<Pick<ColumnMappingDetails, 'legIndex' | 'legProperty'>>
export type MissingMappingData =
    | LegLevelMissingMappingData
    | ({
          name: Exclude<MappedPropertyName, 'mode' | 'countryCode'>
          unmappedValues: string[]
          legProperty: 'transportMode'
      } & Required<Pick<ColumnMappingDetails, 'legIndex'>> &
          (
              | { type: 'group'; groupProperty: Extract<DisplayProperty, { type: 'group' }> }
              | ({ type: 'transportMode' } & Required<
                    Pick<ColumnMappingDetails, 'transportModeType'>
                >)
          ))

export type ColumnMappingDetails = {
    /** Leg's property which is mapped to `value` column mapping property */
    propertyName: string
    /** 0-indexed leg number. Undefined on global properties such as 'load' */
    legIndex?: number
    /** Leg property to which the `propertyName` belongs. Undefined on global properties such as 'load'  */
    legProperty?: keyof TemplateLegInfo
    /** Type of transport which the `propertyName` belongs, expected when property name is 'refrigeration', 'tradelane',
     * etc. */
    transportModeType?: keyof TransportModeMappingData
    /** Column mapping details for 'propertyName' */
    value: ColumnPropertyMapping<unknown>
}

// Auto-fill type that is stored in session storage keeping track of what is autofill
export type AutoFillEntry = {
    column: boolean
    mapping: boolean
}

// Auto-fill type used internally in the component. Has auto fill value + update function
export type AutoFillComponentState = {
    value: boolean
    update: (v: boolean) => void
}

export type AutoFillEntryWithUpdate = {
    column: AutoFillComponentState
    mapping: AutoFillComponentState
}

type RouteAutoFillStateBase<A extends AutoFillEntry | AutoFillEntryWithUpdate> = {
    [K in keyof RouteMappingData]: {
        [K2 in keyof RouteMappingData[K]]: A
    }
}
type TransportModeAutoFillStateBase<A extends AutoFillEntry | AutoFillEntryWithUpdate> = Partial<{
    [T in keyof TransportModeMappingData]: {
        [K in keyof TransportModeMappingData[T]]: A
    }
}>
type AutoFillStateBase<A extends AutoFillEntry | AutoFillEntryWithUpdate> = {
    load?: A
    loadSize?: A
    shippedAt?: A
    shipmentId?: A
    clientAccountName?: A
    clientAccountCurrency?: A
    legs: {
        transportMode: TransportModeAutoFillStateBase<A> & {
            // Used to store inference of transport mode itself.
            mode: { transportMode: A }
        }
        origin: RouteAutoFillStateBase<A>
        destination: RouteAutoFillStateBase<A>
    }[]
}

export type AutoFillState = AutoFillStateBase<AutoFillEntry>
export type AutoFillStateWithUpdate = AutoFillStateBase<AutoFillEntryWithUpdate>

export type LegAutoFill = AutoFillState['legs'][number]
export type LegAutoFillWithUpdate = AutoFillStateWithUpdate['legs'][number]

// We need to initilise the state in our components with autofill data, let's build
// it here to make code in components easier to read.
const emptyFill = { column: false, mapping: false }
const routeEmptyFill = {
    Address: {
        countryCode: emptyFill,
        streetLine: emptyFill,
        city: emptyFill,
        postcode: emptyFill,
    },
    'Airport code': {
        airportCode: emptyFill,
    },
    'Port code': {
        portCode: emptyFill,
    },
    'Geo coordinates': {
        lat: emptyFill,
        lon: emptyFill,
    },
    Distance: {
        distance: emptyFill,
    },
}

const complexSeaMapping = {
    refrigeration: emptyFill,
    tradelane: emptyFill,
    vesselImo: emptyFill,
    vesselName: emptyFill,
    departureOn: emptyFill,
    arrivalOn: emptyFill,
    transitCorridor: emptyFill,
    restrictedTransitCorridor: emptyFill,
}

const complexAirMapping = {
    flightNumber: emptyFill,
    departureOn: emptyFill,
    aircraftType: emptyFill,
}

const complexRoadMapping = {
    fuel: emptyFill,
    refrigeration: emptyFill,
}

export const AUTOFILL_BASIC_EMPTY: LegAutoFill = {
    transportMode: {
        mode: { transportMode: emptyFill },
        'Container Ship': complexSeaMapping,
        'Cargo Plane': complexAirMapping,
        'Passenger Plane': complexAirMapping,
        Plane: complexAirMapping,
        'Inferred truck': complexRoadMapping,
        'Truck rigid 7.5t': complexRoadMapping,
        'Truck rigid 12t': complexRoadMapping,
        'Truck rigid 20t': complexRoadMapping,
        'Truck rigid 26t': complexRoadMapping,
        'Truck rigid 32t': complexRoadMapping,
        'Truck articulated 34t': complexRoadMapping,
        'Truck articulated 40t': complexRoadMapping,
        'Truck articulated 44t': complexRoadMapping,
        'Truck articulated 60t': complexRoadMapping,
        'Truck articulated 72t': complexRoadMapping,
        Truck: {
            ...complexRoadMapping,
            vehicleType: emptyFill,
        },
        'Electric truck': {
            refrigeration: emptyFill,
        },
        'Electric van': {
            refrigeration: emptyFill,
        },
    },
    origin: routeEmptyFill,
    destination: routeEmptyFill,
}

// Transport mode display name from mapping name. Not all modes are mapped
export const TRANSPORT_MODE_LABEL: { [k in TransportMode]?: string } = {
    Truck: 'Van generic',
}

export type KeysOfUnion<T> = T extends any ? keyof T : never
export type TransportModeProperty = KeysOfUnion<TransportModeMappingData[TransportMode]>

export const TRANSPORT_MODES_PER_GROUP_PROPERTY = {
    Road: {
        fuel: [
            'Inferred truck',
            'Truck',
            'Truck rigid 7.5t',
            'Truck rigid 12t',
            'Truck rigid 20t',
            'Truck rigid 26t',
            'Truck rigid 32t',
            'Truck articulated 34t',
            'Truck articulated 40t',
            'Truck articulated 44t',
            'Truck articulated 60t',
            'Truck articulated 72t',
        ],
        refrigeration: complexRoadTransportModeArray,
    },
    Sea: {},
    Air: {
        aircraftType: ['Plane', 'Cargo Plane', 'Passenger Plane'],
        flightNumber: ['Plane', 'Cargo Plane', 'Passenger Plane'],
        departureOn: ['Plane', 'Cargo Plane', 'Passenger Plane'],
    },
    Rail: {},
    'Inland waterways': {},
} as const satisfies Record<
    TransportModeGroup,
    { [t in TransportModeProperty]?: readonly TransportMode[] }
>

export type DisplayProperty =
    | {
          type: 'transportMode'
          transportMode: TransportMode
          parameter: TransportModeProperty
      }
    | {
          type: 'group'
          group: TransportModeGroup
          parameter: TransportModeProperty
      }
